"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@/libs/utils";
import IndeterminateIcon from "@/v2-components/ui/_icons/indeterminate-icon";
import CheckedIcon from "@/v2-components/ui/_icons/checked-icon";
import { cva } from "class-variance-authority";

const checkboxVariants = cva([
  "peer",
  "w-4.5 h-4.5 p-[2px] border shrink-0 rounded-[3px]",
  "border-coolgray-90 data-[state=checked]:bg-coolgray-90 data-[state=indeterminate]:bg-coolgray-90",
  "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-60 focus-visible:ring-offset-1",
  "disabled:cursor-not-allowed disabled:border-coolgray-30 disabled:data-[state=checked]:bg-coolgray-30 disabled:data-[state=indeterminate]:bg-coolgray-30",
]);

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, defaultChecked, checked, onCheckedChange, ...props }, ref) => {
  const [_checked, setChecked] = React.useState<boolean | "indeterminate">(
    defaultChecked ?? checked ?? false,
  );

  /**
   * ⚠️ `checked` 를 넘기면 **제어 컴포넌트**로 동작해야 한다.
   *
   * 종전엔 내부 state 를 마운트 시 한 번만 초기화하고 그 뒤로는 자기 `onCheckedChange`
   * 로만 갱신했다. 그래서 **바깥에서 값을 바꿔도 표시가 안 따라왔다** — 화면이 실제
   * 상태를 거짓말한다.
   *
   * 실측(2026-08-31, `/games/vr/games`): VIVE 필터를 켠 뒤 「초기화」를 누르면 URL 도
   * 목록도 초기화되는데 **체크 표시만 켜진 채로 남았다**(`data-state="checked"`).
   *
   * 호출처 19곳을 태그 단위로 분류하면:
   *
   * ```
   *   checked= 전달 (제어)        11   ← prop 을 따른다
   *   defaultChecked= 전달         0   ← 리포에 **없다**
   *   둘 다 없음 (비제어)           8   ← 전부 react-hook-form `{...register()}` 스프레드
   * ```
   *
   * 비제어 경로의 대상은 「`defaultChecked` 를 준 곳」이 아니라 **「`checked` 를 안 준 곳」**
   * 이다. `register()` 는 `checked` 를 돌려주지 않으므로 8곳 다 `isControlled === false` 로
   * 떨어져 종전 경로를 그대로 탄다.
   */
  const isControlled = checked !== undefined;
  const value = isControlled ? checked : _checked;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants(), className)}
      checked={value}
      onCheckedChange={(next) => {
        // 비제어일 때만 내부 state 를 움직인다. 제어면 호출처가 prop 으로 되돌려준다.
        if (!isControlled) setChecked(next);
        onCheckedChange?.(next);
      }}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {value === "indeterminate" && (
          <IndeterminateIcon className="h-auto w-auto text-white" />
        )}
        {value === true && <CheckedIcon className="h-auto w-auto text-white" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
